import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelpCircle, Key, Cpu, Pencil, Unlink, QrCode } from "lucide-react";
import ApiKeysTab from "@/components/apikeys/ApiKeysTab";
import IngestUsageCard from "@/components/usage/IngestUsageCard";
import { MIN_PASSWORD_LENGTH } from "@/config/passwordPolicy";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { apiClient } from "@/services/api";
import Modal from "@/components/ui/Modal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import DeviceLinkQrScanner from "@/components/devices/DeviceLinkQrScanner";
import type { UserVisibility, UserRole, QuotaUsage, Device, DeviceType, UpdateDeviceRequest } from "@/types";

type Tab = "myInfo" | "myDevices" | "myApiKeys";

const VALID_TABS: Tab[] = ["myInfo", "myDevices", "myApiKeys"];

const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  sensor: "deviceTypeSensor",
  telemetry: "deviceTypeTelemetry",
  video: "deviceTypeVideo",
  audio: "deviceTypeAudio",
  other: "deviceTypeOther",
};

const USER_ROLES: UserRole[] = [
  "driver",
  "director",
  "coach",
  "mechanic",
  "staff",
  "marshal",
  "official",
  "other",
];

function getFormString(
  formData: FormData,
  name: string,
  fallback: string,
): string {
  const value = formData.get(name);
  return typeof value === "string" ? value : fallback;
}

function getFormVisibility(
  formData: FormData,
  fallback: UserVisibility,
): UserVisibility {
  const value = formData.get("visibility");
  return value === "public" || value === "private" ? value : fallback;
}

function getFormRole(formData: FormData, fallback: UserRole): UserRole {
  const value = formData.get("role");
  return typeof value === "string" && USER_ROLES.includes(value as UserRole)
    ? (value as UserRole)
    : fallback;
}

export default function MyPage() {
  const { user, updateUser, setSession } = useAuth();
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  const activeTab: Tab = VALID_TABS.includes(rawTab as Tab) ? (rawTab as Tab) : "myInfo";

  const setActiveTab = (tab: Tab) => {
    setSearchParams(tab === "myInfo" ? {} : { tab }, { replace: true });
  };

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [visibility, setVisibility] = useState<UserVisibility>("private");
  const [role, setRole] = useState<UserRole>("driver");
  const [isSaving, setIsSaving] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [quota, setQuota] = useState<QuotaUsage | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  // Change password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {},
  );
  const [isChangingPassword, setIsChangingPassword] = useState(false);

// Devices state
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [showLinkDeviceModal, setShowLinkDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [editDeviceName, setEditDeviceName] = useState("");
  const [editDeviceType, setEditDeviceType] = useState<DeviceType>("other");
  const [isSavingDevice, setIsSavingDevice] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<Device | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  
  useEffect(() => {
    if (user) {
      setGivenName(user.givenName);
      setFamilyName(user.familyName);
      setVisibility(user.visibility ?? "private");
      setRole(user.role ?? "driver");
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchQuota = async () => {
      try {
        const data = await apiClient.getUserQuota(user.id);
        setQuota(data);
      } catch {
        // Silently fail — quota is optional display
      } finally {
        setQuotaLoading(false);
      }
    };
    fetchQuota();
  }, [user]);

  const fetchDevices = useCallback(async () => {
    if (!user) return;
    setDevicesLoading(true);
    try {
      const data = await apiClient.getDevicesByUser(user.id);
      setDevices(data);
    } catch {
      // Silently fail — device list is optional
    } finally {
      setDevicesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "myDevices") {
      fetchDevices();
    }
  }, [activeTab, fetchDevices]);

  const handleProfileSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    const formData = new FormData(e.currentTarget);
    const nextGivenName = getFormString(formData, "givenName", givenName);
    const nextFamilyName = getFormString(formData, "familyName", familyName);
    const nextVisibility = getFormVisibility(formData, visibility);
    const nextRole = getFormRole(formData, role);

    setIsSaving(true);
    try {
      const updatedUser = await apiClient.updateProfile(user.id, {
        givenName: nextGivenName,
        familyName: nextFamilyName,
        visibility: nextVisibility,
        role: nextRole,
      });
      updateUser(updatedUser);
      toast.success(t("auth:profileUpdated"));
    } catch {
      toast.error(t("auth:profileUpdateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const hasProfileChanges =
    user &&
    (givenName !== user.givenName ||
      familyName !== user.familyName ||
      visibility !== (user.visibility ?? "private") ||
      role !== (user.role ?? "driver"));

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!currentPassword) {
      errors.currentPassword = t("auth:passwordRequired");
    }
    if (!newPassword) {
      errors.newPassword = t("auth:passwordRequired");
    } else if (newPassword.length < MIN_PASSWORD_LENGTH) {
      errors.newPassword = t("auth:passwordTooShort");
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = t("auth:passwordMismatch");
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    try {
      const { user, token, refreshToken } =
        await apiClient.changePasswordAuthenticated({
          currentPassword,
          newPassword,
        });
      setSession(user, token, refreshToken);
      toast.success(t("auth:passwordChanged"));
      setShowPasswordModal(false);
      resetPasswordForm();
    } catch (error: unknown) {
      const apiError = error as {
        response?: {
          data?: { error?: { code?: string; message?: string } };
        };
      };

      if (apiError.response?.data?.error?.code === "INCORRECT_PASSWORD") {
        setPasswordErrors({
          currentPassword: t("auth:incorrectCurrentPassword"),
        });
      } else {
        toast.error(t("auth:passwordChangeError"));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({});
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    resetPasswordForm();
  };

const handleOpenEditDevice = (device: Device) => {
    setEditingDevice(device);
    setEditDeviceName(device.name ?? "");
    setEditDeviceType(device.type);
  };

  const handleSaveDevice = async () => {
    if (!editingDevice) return;
    setIsSavingDevice(true);
    try {
      const trimmedName = editDeviceName.trim();
      const updateData: UpdateDeviceRequest = {
        name: trimmedName.length > 0 ? trimmedName : null,
        type: editDeviceType,
      };
      const updated = await apiClient.updateDevice(editingDevice.id, updateData);
      setDevices((prev) =>
        prev.map((d) => (d.id === updated.id ? updated : d)),
      );
      setEditingDevice(null);
      toast.success(t("auth:deviceUpdated"));
    } catch {
      toast.error(t("auth:deviceUpdateError"));
    } finally {
      setIsSavingDevice(false);
    }
  };

  const handleUnlinkDevice = async () => {
    if (!unlinkTarget) return;
    setIsUnlinking(true);
    try {
      await apiClient.unlinkDevice(unlinkTarget.id);
      setDevices((prev) => prev.filter((d) => d.id !== unlinkTarget.id));
      setUnlinkTarget(null);
      toast.success(t("auth:deviceUnlinked"));
    } catch {
      toast.error(t("auth:deviceUnlinkError"));
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleOpenLinkDeviceModal = () => {
    setShowLinkDeviceModal(true);
  };

  const handleCloseLinkDeviceModal = () => {
    setShowLinkDeviceModal(false);
  };

  const handleDeviceLinkCodeScanned = useCallback((code: string) => {
    setShowLinkDeviceModal(false);
    navigate(`/link-device?code=${encodeURIComponent(code)}`);
  }, [navigate]);

  if (!user) return null;

  return (
 <div className="mx-auto w-full max-w-2xl px-[var(--shell-gutter-x)]">
 <h1 className="page-title mb-3 sm:mb-8">
        {t("auth:myPage")}
      </h1>

      {/* Tab navigation */}
 <div className="flex border-b border-secondary-200 mb-6">
        {(["myInfo", "myDevices", "myApiKeys"] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
 className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-secondary-600 hover:text-secondary-900"
            }`}
          >
            {t(`auth:tab${tab.charAt(0).toUpperCase()}${tab.slice(1)}`)}
          </button>
        ))}
      </div>

      {/* My Info tab */}
      {activeTab === "myInfo" && (
 <form onSubmit={handleProfileSubmit} className="space-y-6">
          {/* Email (read-only) */}
          <div>
 <label className="block text-sm font-medium text-secondary-700 mb-1">
              {t("auth:email")}
            </label>
            <input
              type="email"
              value={user.email}
              disabled
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-secondary-50 text-secondary-500 cursor-not-allowed"
            />
          </div>

          {/* Given Name */}
          <div>
            <label
              htmlFor="givenName"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:givenName")}
            </label>
            <input
              id="givenName"
              name="givenName"
              type="text"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              required
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Family Name */}
          <div>
            <label
              htmlFor="familyName"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:familyName")}
            </label>
            <input
              id="familyName"
              name="familyName"
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              required
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Role */}
          <div>
            <label
              htmlFor="role"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:role")}
            </label>
            <select
              id="role"
              name="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="driver">{t("auth:roleDriver")}</option>
              <option value="director">{t("auth:roleDirector")}</option>
              <option value="coach">{t("auth:roleCoach")}</option>
              <option value="mechanic">{t("auth:roleMechanic")}</option>
              <option value="staff">{t("auth:roleStaff")}</option>
              <option value="marshal">{t("auth:roleMarshal")}</option>
              <option value="official">{t("auth:roleOfficial")}</option>
              <option value="other">{t("auth:roleOther")}</option>
            </select>
          </div>

          {/* Visibility */}
          <div>
 <div className="flex items-center space-x-1 mb-1">
 <label className="block text-sm font-medium text-secondary-700">
                {t("auth:visibility")}
              </label>
 <div className="relative">
                <button
                  type="button"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={() => setShowTooltip(true)}
 className="text-secondary-400 hover:text-secondary-600"
                  aria-label={t("auth:visibilityHelp")}
                >
 <HelpCircle className="h-4 w-4" />
                </button>
                {showTooltip && (
 <div className="absolute left-6 top-0 z-10 w-64 p-2 text-xs text-secondary-700 bg-white border border-secondary-200 rounded-lg shadow-lg">
                    {t("auth:visibilityTooltip")}
                  </div>
                )}
              </div>
            </div>
 <div className="flex space-x-4">
 <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === "public"}
                  onChange={() => setVisibility("public")}
 className="text-primary-600 focus:ring-primary-500"
                />
 <span className="text-sm text-secondary-700">
                  {t("auth:visibilityPublic")}
                </span>
              </label>
 <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === "private"}
                  onChange={() => setVisibility("private")}
 className="text-primary-600 focus:ring-primary-500"
                />
 <span className="text-sm text-secondary-700">
                  {t("auth:visibilityPrivate")}
                </span>
              </label>
            </div>
          </div>

          {/* Monthly usage */}
          {quota && !quotaLoading && (
            <div className="pt-4 border-t border-secondary-200">
              <IngestUsageCard quota={quota} />
            </div>
          )}

          {/* Save button */}
 <div className="flex items-center justify-between pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
 className="flex items-center space-x-2 text-sm text-secondary-600 hover:text-secondary-900 px-4 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
 <Key className="h-4 w-4" />
              <span>{t("auth:changePassword")}</span>
            </button>

            <button
              type="submit"
              disabled={isSaving || !hasProfileChanges}
 className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t("common:loading") : t("common:save")}
            </button>
          </div>
        </form>
      )}

      {/* My Devices tab */}
      {activeTab === "myDevices" && (
 <div className="space-y-4">
 <div className="flex justify-end">
<button
              type="button"
              onClick={handleOpenLinkDeviceModal}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <QrCode className="h-4 w-4" />
              <span>{t("auth:linkDevice")}</span>
            </button>
          </div>

          {devicesLoading && (
 <p className="text-sm text-secondary-500">{t("common:loading")}</p>
          )}

          {!devicesLoading && devices.length === 0 && (
 <div className="text-center py-12 text-secondary-500">
 <Cpu className="h-10 w-10 mx-auto mb-3 opacity-40" />
 <p className="text-sm">{t("auth:deviceListEmpty")}</p>
            </div>
          )}

          {!devicesLoading && devices.length > 0 && (
 <ul className="divide-y divide-secondary-200 border border-secondary-200 rounded-lg overflow-hidden">
              {devices.map((device) => (
<li key={device.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-secondary-50">
 <div className="min-w-0 flex-1">
 <div className="flex items-center space-x-2">
 <span className="text-sm font-medium text-secondary-900 capitalize">
                        {t(`auth:deviceType${device.type.charAt(0).toUpperCase()}${device.type.slice(1)}` as Parameters<typeof t>[0])}
                      </span>
                    </div>
                    {device.name && (
 <p className="text-sm font-medium text-secondary-800 mt-1 truncate">
                        {device.name}
                      </p>
                    )}
 <p className="text-xs text-secondary-500 mt-0.5">
                      {device.linkedAt
                        ? t("auth:deviceLinkedAt", {
                            date: new Date(device.linkedAt).toLocaleDateString(),
                          })
                        : t("auth:deviceNotLinked")}
                    </p>
                  </div>
 <div className="flex items-center space-x-1 ml-4 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleOpenEditDevice(device)}
 className="p-1.5 text-secondary-400 hover:text-secondary-700 rounded"
                      aria-label={t("auth:editDevice")}
                    >
 <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setUnlinkTarget(device)}
 className="p-1.5 text-secondary-400 hover:text-red-600 rounded"
                      aria-label={t("auth:unlinkDevice")}
                    >
 <Unlink className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* API Keys tab */}
      {activeTab === "myApiKeys" && (
        <ApiKeysTab />
      )}

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={handleClosePasswordModal}
        title={t("auth:changePassword")}
        size="sm"
      >
 <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="currentPassword"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:currentPassword")}
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                passwordErrors.currentPassword
                  ? "border-red-300"
                  : "border-secondary-300"
              }`}
            />
            {passwordErrors.currentPassword && (
 <p className="mt-1 text-sm text-red-600">
                {passwordErrors.currentPassword}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="newPassword"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:newPassword")}
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                passwordErrors.newPassword
                  ? "border-red-300"
                  : "border-secondary-300"
              }`}
            />
            {passwordErrors.newPassword && (
 <p className="mt-1 text-sm text-red-600">
                {passwordErrors.newPassword}
              </p>
            )}
 <p className="mt-1 text-xs text-secondary-500">
              {t("auth:passwordRequirement")}
            </p>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
 className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:confirmPassword")}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                passwordErrors.confirmPassword
                  ? "border-red-300"
                  : "border-secondary-300"
              }`}
            />
            {passwordErrors.confirmPassword && (
 <p className="mt-1 text-sm text-red-600">
                {passwordErrors.confirmPassword}
              </p>
            )}
          </div>

 <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClosePasswordModal}
 className="px-4 py-2 text-sm text-secondary-700 border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              {t("common:cancel")}
            </button>
            <button
              type="submit"
              disabled={isChangingPassword}
 className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isChangingPassword
                ? t("common:loading")
                : t("auth:changePassword")}
            </button>
          </div>
        </form>
      </Modal>

{/* Link a Device Modal */}
      <Modal
        isOpen={showLinkDeviceModal}
        onClose={handleCloseLinkDeviceModal}
        title={t("auth:linkDevice")}
        size="md"
      >
        <div className="space-y-5">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl border border-secondary-200 bg-secondary-50 text-primary-600">
            <QrCode className="h-7 w-7" />
          </div>
          <p className="text-center text-sm leading-6 text-secondary-600">
            {t("auth:linkDeviceDescription")}
          </p>

          {showLinkDeviceModal && (
            <DeviceLinkQrScanner onCodeScanned={handleDeviceLinkCodeScanned} />
          )}

          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={handleCloseLinkDeviceModal}
              className="btn-secondary px-4 py-2"
            >
              {t("common:ok")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Device Modal */}
      <Modal
        isOpen={editingDevice !== null}
        onClose={() => setEditingDevice(null)}
        title={t("auth:editDevice")}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="editDeviceName"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:deviceNameLabel")}
            </label>
            <input
              id="editDeviceName"
              type="text"
              maxLength={255}
              value={editDeviceName}
              onChange={(e) => setEditDeviceName(e.target.value)}
              placeholder={t("auth:deviceNamePlaceholder")}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="editDeviceType"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              {t("auth:deviceTypeLabel")}
            </label>
            <select
              id="editDeviceType"
              value={editDeviceType}
              onChange={(e) => setEditDeviceType(e.target.value as DeviceType)}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              {(Object.keys(DEVICE_TYPE_LABELS) as DeviceType[]).map((dt) => (
                <option key={dt} value={dt}>
                  {t(`auth:${DEVICE_TYPE_LABELS[dt]}` as Parameters<typeof t>[0])}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setEditingDevice(null)}
              className="px-4 py-2 text-sm text-secondary-700 border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              {t("common:cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveDevice}
              disabled={isSavingDevice}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isSavingDevice ? t("common:loading") : t("common:save")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Unlink Confirmation Modal */}
      <ConfirmationModal
        isOpen={unlinkTarget !== null}
        onClose={() => setUnlinkTarget(null)}
        onConfirm={handleUnlinkDevice}
        title={t("auth:unlinkDevice")}
        message={t("auth:unlinkDeviceConfirm")}
        confirmButtonText={t("auth:unlinkDevice")}
        confirmButtonColor="red"
        isLoading={isUnlinking}
      />
    </div>
  );
}
